import { RollbackOutlined } from '@ant-design/icons';
import { Col, Dropdown, Row, Space, Tag, Tooltip } from 'antd';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Routes } from '../../../../app/routes';
import CustomButton from '../../../../shared/components/buttons/custom-button/custom-button';
import CollapsablePreview from '../../../../shared/components/collapsable-preview/collapsable-preview';
import PdfViewer from '../../../../shared/components/pdf-viewer/pdf-viewer';
import { useBreakpoint } from '../../../../shared/hooks/use-breakpoint';
import { useHasPermission } from '../../../../shared/hooks/use-has-permission';
import { ArchiveIcon } from '../../../../shared/icons/archive-icon';
import { ArrowUpRightIcon } from '../../../../shared/icons/arrow-up-right-icon';
import { BoxArrowUpRightIcon } from '../../../../shared/icons/box-arrow-up-right-icon';
import { ClockHistoryIcon } from '../../../../shared/icons/clock-history-icon';
import { EmailIcon } from '../../../../shared/icons/email-icon';
import { MoreIcon } from '../../../../shared/icons/more-icon';
import { TrashIcon } from '../../../../shared/icons/trash-icon';
import { UnionIcon } from '../../../../shared/icons/union-icon';
import { convertServerDateTimeString } from '../../../../shared/utils/format-date/format-date';
import { useAppSelector } from '../../../../store/hooks';
import RequestPreviewContentData from '../../../request/components/request-preview-content-data/request-preview-content-data';
import { RequestDetails } from '../../../request/models/request-details';
import { openModal } from '../../../ui/store/ui-slice';
import { UserPermissionType } from '../../../user/models/user-permission-type';
import {
  signRequestArchiveModalId,
  signRequestDeleteModalId,
  signRequestDetachModalId,
  signRequestEmailModalId,
  signRequestMetadataModalId,
  signRequestMoveModalId,
  signRequestPreviewUpdateModalId,
  signRequestRestoreModalId,
} from '../../constants/constants';
import { SignRequestStatus } from '../../models/sign-request-status';
import {
  selectActiveSignRequestId,
  selectTempSignRequestId,
  setTempSignRequestId,
} from '../../store/sign-requests-slice';
import { getSignDocumentFileUrl } from '../../utils/get-sign-document-file-url';
import SignRequestDeleteModal from '../sign-request-delete-modal/sign-request-delete-modal';
import SignRequestDetachModal from '../sign-request-detach-modal/sign-request-detach-modal';
import SignRequestEmailFormModal from '../sign-request-email-form-modal/sign-request-email-form-modal';
import SignRequestMetadataModal from '../sign-request-metadata-modal/sign-request-metadata-modal';
import SignRequestMoveFormModal from '../sign-request-move-form-modal/sign-request-move-form-modal';
import SignRequestRestoreModal from '../sign-request-restore-modal/sign-request-restore-modal';
import SignRequestsPreviewUpdateModal from '../sign-requests-preview-update-modal/sign-requests-preview-update-modal';
import styles from './sign-requests-preview.module.css';
import { useGetUserSettingsQuery } from '../../../user/api/user-api';
import { UserSettings } from '../../../user/models/user-settings';
import { hasPermission } from '../../../../shared/utils/has-permission';
import { User } from '../../../user/models/user';

interface Props {
  requestItem: RequestDetails;
  currentUser?: User;
}

const SignRequestsPreview = ({ requestItem, currentUser }: Props): JSX.Element => {
  const dispatch = useDispatch();
  const activeSignRequestId = useAppSelector(selectActiveSignRequestId);
  const activeTempSignRequestId = useAppSelector(selectTempSignRequestId);
  const screens = useBreakpoint();
  const { data: userSettings } = useGetUserSettingsQuery();
  const { t } = useTranslation(['sign-requests', 'common']);
  const hasPermissionArchiveDocuments = useHasPermission(UserPermissionType.ArchiveDocuments);

  const handleOpenModal = (modalId: string, signRequestId: string) => {
    dispatch(openModal(modalId));
    dispatch(setTempSignRequestId(signRequestId));
  };

  return (
    <Space direction="vertical" size={20} className={styles.collapseContainer}>
      {requestItem?.signRequests.map(signRequest => (
        <CollapsablePreview
          className={styles.parentCollapseBg}
          title={`${signRequest.name || t('label.signRequest', { ns: 'common' })} - ${
            signRequest.signedAt
              ? convertServerDateTimeString(signRequest.signedAt, userSettings?.[UserSettings.DateFormat])
              : signRequest.status === SignRequestStatus.Pending
                ? t('signScanRequest.label.statusOption.pending', { ns: 'common' })
                : ''
          }`}
          index={signRequest.id}
          key={signRequest.id}
          icon={<UnionIcon />}
          defaultActiveKey={activeTempSignRequestId || activeSignRequestId || requestItem?.signRequests[0].id}
        >
          <>
            {!screens.xs && (
              <RequestPreviewContentData
                contentData={[
                  {
                    title: userSettings?.[UserSettings.SignRequestNameTitle] || t('label.name', { ns: 'common' }),
                    data: signRequest.name,
                    onEdit: () => handleOpenModal(signRequestPreviewUpdateModalId, signRequest.id),
                  },
                  {
                    title: t('label.status', { ns: 'common' }),
                    data: (
                      <Tag>
                        {signRequest.status === SignRequestStatus.Signed
                          ? t('statusOption.signed')
                          : signRequest.status === SignRequestStatus.Pending
                            ? t('signScanRequest.label.statusOption.pending', { ns: 'common' })
                            : ''}
                      </Tag>
                    ),
                  },
                  {
                    title: t('signRequestsPreview.signed'),
                    data:
                      signRequest.signedAt &&
                      convertServerDateTimeString(signRequest.signedAt, userSettings?.[UserSettings.DateFormat]),
                  },
                  {
                    title: t('label.appUser'),
                    data: signRequest.clientUser?.name || '',
                  },

                  {
                    title: t('label.customer', { ns: 'common' }),
                    data: signRequest.customer?.name || '',
                    hidden: true,
                  },

                  {
                    title: t('label.template', { ns: 'common' }),
                    data: signRequest.template?.name || '',
                  },
                  ...(signRequest.documents.length < 2
                    ? [
                        {
                          title: t('signRequestsPreview.fileName'),
                          data: signRequest.documents[0].name,
                        },
                      ]
                    : []),
                ]}
              />
            )}
            <Row className={clsx(styles.actionButtonRow, styles.documentActionRow)} justify="start" gutter={[10, 10]}>
              {signRequest.documents.length < 2 && (
                <>
                  {!screens.xs && (
                    <Col>
                      <CustomButton
                        size="small"
                        type="darkBlue"
                        fontWeight={400}
                        fontSize={12}
                        onClick={() =>
                          dispatch(
                            openModal({
                              id: signRequestMetadataModalId,
                              props: { documentId: signRequest.documents[0].id },
                            }),
                          )
                        }
                      >
                        {t('signRequestsPreview.action.viewMetadata')}
                      </CustomButton>
                    </Col>
                  )}
                  <Col>
                    <a
                      href={getSignDocumentFileUrl(signRequest.documents[0].id, true)}
                      download
                      className={clsx({ [styles.downloadActionDisabled]: signRequest.deleted })}
                    >
                      <CustomButton
                        size="small"
                        type="primaryLight"
                        fontWeight={400}
                        fontSize={12}
                        disabled={signRequest.deleted}
                      >
                        {t('action.download', { ns: 'common' })}
                      </CustomButton>
                    </a>
                  </Col>
                </>
              )}
              {!screens.xs && (
                <>
                  <Col>
                    <CustomButton
                      size="small"
                      type="primaryLight"
                      fontWeight={400}
                      fontSize={12}
                      icon={<EmailIcon />}
                      onClick={() => handleOpenModal(signRequestEmailModalId, signRequest.id)}
                      disabled={signRequest.deleted || signRequest.status === SignRequestStatus.Pending}
                    >
                      {t('label.email', { ns: 'common' })}
                    </CustomButton>
                  </Col>
                  {signRequest.deleted && (
                    <Col>
                      <CustomButton
                        size="small"
                        type="textBg"
                        fontWeight={400}
                        fontSize={12}
                        icon={<RollbackOutlined />}
                        onClick={() => handleOpenModal(signRequestRestoreModalId, signRequest.id)}
                      >
                        {t('signScanRequest.action.restore', { ns: 'common' })}
                      </CustomButton>
                    </Col>
                  )}
                  <Col className={styles.dropdownColumn}>
                    <Dropdown
                      arrow
                      placement="bottomRight"
                      classNames={{ root: styles.actionsDropdown }}
                      trigger={[screens.lg ? 'hover' : 'click']}
                      menu={{
                        items: [
                          {
                            key: 'history',
                            label: (
                              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                              <Link to={Routes.SignRequestTraceLogs.replace(':id', activeSignRequestId!)}>
                                <CustomButton
                                  size="small"
                                  fontWeight={400}
                                  fontSize={12}
                                  type="textBg"
                                  icon={<ClockHistoryIcon />}
                                >
                                  {t('action.history', { ns: 'common' })}
                                </CustomButton>
                              </Link>
                            ),
                          },
                          {
                            key: 'detach',
                            label: (
                              <CustomButton
                                size="small"
                                type="textBg"
                                fontWeight={400}
                                fontSize={12}
                                icon={<BoxArrowUpRightIcon />}
                                onClick={() => handleOpenModal(signRequestDetachModalId, signRequest.id)}
                                disabled={
                                  !(
                                    requestItem.signRequests.length > 1 ||
                                    requestItem.scanRequests.length > 1 ||
                                    (requestItem.signRequests.length > 0 && requestItem.scanRequests.length > 0)
                                  ) || signRequest.deleted
                                }
                              >
                                {t('signScanRequest.action.detach', { ns: 'common' })}
                              </CustomButton>
                            ),
                          },
                          {
                            key: 'move',
                            label: (
                              <CustomButton
                                size="small"
                                type="textBg"
                                fontWeight={400}
                                fontSize={12}
                                icon={<ArrowUpRightIcon />}
                                onClick={() => handleOpenModal(signRequestMoveModalId, signRequest.id)}
                                disabled={signRequest.deleted}
                              >
                                {t('signScanRequest.action.move', { ns: 'common' })}
                              </CustomButton>
                            ),
                          },
                          ...(signRequest.canArchive
                            ? [
                                {
                                  key: 'uploadToServer',
                                  label: (
                                    <CustomButton
                                      size="small"
                                      type="textBg"
                                      fontWeight={400}
                                      fontSize={12}
                                      icon={<ArchiveIcon />}
                                      onClick={() => handleOpenModal(signRequestArchiveModalId, signRequest.id)}
                                      disabled={!hasPermissionArchiveDocuments}
                                    >
                                      {t('signScanRequest.action.uploadToServer', { ns: 'common' })}
                                    </CustomButton>
                                  ),
                                },
                              ]
                            : []),
                          ...(!signRequest.deleted
                            ? [
                                {
                                  key: 'delete',
                                  label: (
                                    <Tooltip
                                      mouseEnterDelay={0.5}
                                      title={
                                        currentUser &&
                                        !hasPermission(currentUser, UserPermissionType.DeleteDocuments) &&
                                        t('hint.noPermissionDeleteDocument', { ns: 'common' })
                                      }
                                    >
                                      <CustomButton
                                        size="small"
                                        danger
                                        type="default"
                                        fontWeight={400}
                                        fontSize={12}
                                        disabled={
                                          currentUser && !hasPermission(currentUser, UserPermissionType.DeleteDocuments)
                                        }
                                        icon={<TrashIcon />}
                                        onClick={() => handleOpenModal(signRequestDeleteModalId, signRequest.id)}
                                      >
                                        {t('action.delete', { ns: 'common' })}
                                      </CustomButton>
                                    </Tooltip>
                                  ),
                                },
                              ]
                            : []),
                        ],
                      }}
                    >
                      <CustomButton size="small" icon={<MoreIcon />} className="btn-only-icon" type="secondaryLight" />
                    </Dropdown>
                  </Col>
                </>
              )}
            </Row>
            <div className={styles.childCollapseWrapper}>
              {signRequest.documents.length > 1 ? (
                signRequest.documents.map((document, index) => (
                  <CollapsablePreview
                    title={signRequest.documents[index]?.name}
                    index={signRequest.documents[index]?.id}
                    key={signRequest.documents[index]?.id}
                    defaultActiveKey={signRequest.documents[0]?.id}
                  >
                    {!screens.xs && (
                      <RequestPreviewContentData
                        contentData={[
                          {
                            title: t('label.name', { ns: 'common' }),
                            data: signRequest.documents[index].name,
                          },
                          {
                            title: t('label.status', { ns: 'common' }),
                            data: <Tag>{signRequest.documents[index].status}</Tag>,
                          },
                          {
                            title: t('signScanRequest.label.signedAt', { ns: 'common' }),
                            data:
                              signRequest.signedAt &&
                              convertServerDateTimeString(
                                signRequest.signedAt,
                                userSettings?.[UserSettings.DateFormat],
                              ),
                          },
                        ]}
                      />
                    )}
                    <Row className={styles.actionButtonRow} justify="end" gutter={[10, 10]}>
                      {!screens.xs && (
                        <Col>
                          <CustomButton
                            size="small"
                            type="darkBlue"
                            fontWeight={400}
                            fontSize={12}
                            onClick={() =>
                              dispatch(
                                openModal({
                                  id: signRequestMetadataModalId,
                                  props: { documentId: document.id },
                                }),
                              )
                            }
                            disabled={signRequest.deleted}
                          >
                            {t('signRequestsPreview.action.viewMetadata')}
                          </CustomButton>
                        </Col>
                      )}
                      <Col>
                        <a
                          href={getSignDocumentFileUrl(document.id, true)}
                          className={clsx({ [styles.downloadActionDisabled]: signRequest.deleted })}
                          download
                        >
                          <CustomButton
                            size="small"
                            type="primaryLight"
                            fontWeight={400}
                            fontSize={12}
                            disabled={signRequest.deleted}
                          >
                            {t('action.download', { ns: 'common' })}
                          </CustomButton>
                        </a>
                      </Col>
                    </Row>
                    <PdfViewer key={index} pdfSource={getSignDocumentFileUrl(document.id)} />
                  </CollapsablePreview>
                ))
              ) : (
                <PdfViewer pdfSource={getSignDocumentFileUrl(signRequest.documents[0].id)} />
              )}
            </div>
          </>
        </CollapsablePreview>
      ))}
      <SignRequestMoveFormModal />
      <SignRequestMetadataModal />
      <SignRequestEmailFormModal />
      <SignRequestDetachModal />
      <SignRequestDeleteModal />
      <SignRequestRestoreModal />
      <SignRequestsPreviewUpdateModal
        formId={signRequestPreviewUpdateModalId}
        inputsData={{
          name: {
            initialValue:
              requestItem.signRequests.find(signRequest => signRequest.id === activeSignRequestId)?.name || '',
            label:
              userSettings?.[UserSettings.SignRequestNameTitle] ||
              t('signScanRequest.label.documentName', { ns: 'common' }),
            placeholder:
              userSettings?.[UserSettings.SignRequestNameTitle] ||
              t('signScanRequest.label.documentName', { ns: 'common' }),
          },
        }}
      />
    </Space>
  );
};
export default SignRequestsPreview;
